import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, Input } from "@/design-system";
import { Icon } from "@/components/Icon";
import { PasswordInput } from "@/components/PasswordInput";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ApiRequestError, api } from "@/api/client";
import { useAuth } from "@/auth";
import type { AuthResponse } from "@/lib/types";
// Bundler import (Vite inlines the asset) — the raw `/src/...` string
// used to force a runtime network fetch, which broke offline (issue #106).
import logoMark from "@/design-system/assets/logo-mark.svg";

/**
 * Auth screen — login + register on a single page.
 *
 * - `initialMode="login"` → `POST /api/session`, redirects by role
 *   (teacher → `/teacher`, student → `/calendar`).
 * - `initialMode="register"` → `POST /api/users/register`, auto-logs
 *   the new student in and sends them to `/calendar`.
 *
 * The API calls are issued as `@tanstack/react-query` mutations.
 *
 * Error display (per the API contract):
 * - 401 → a general "That email and password didn't match" message
 *   above the submit button (rendered as a design-system Card with
 *   danger styling).
 * - 422 → per-field errors, surfaced inline on the corresponding
 *   `<Input>` via its `invalid` flag + `hint` slot.
 * - Anything else → a friendly fallback message.
 *
 * Loading state: the submit button is disabled and shows a
 * mode-specific "Signing in…" / "Creating account…" label while the
 * mutation is in flight. The inputs are also disabled to prevent
 * edits mid-submit.
 */
type Mode = "login" | "register";

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
}

const initialFieldErrors: FieldErrors = {};

export function AuthScreen({ initialMode = "login" }: { initialMode?: Mode }) {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Set by ResetPasswordScreen after a successful reset
  // (`/login?reset=1`) — the user arrives knowing their old password
  // no longer works, so show a confirmation instead of a failure.
  const showResetSuccess = searchParams.get("reset") === "1";

  const [mode, setMode] = useState<Mode>(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] =
    useState<FieldErrors>(initialFieldErrors);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const loginMutation = useMutation<AuthResponse, Error, { email: string; password: string }>({
    mutationFn: (creds) =>
      api.post<AuthResponse>("/api/session", creds, { token: null }),
  });

  const registerMutation = useMutation<
    AuthResponse,
    Error,
    { name: string; email: string; password: string }
  >({
    mutationFn: (creds) =>
      api.post<AuthResponse>("/api/users/register", creds, { token: null }),
  });

  const mutation = mode === "login" ? loginMutation : registerMutation;
  const isPending = mutation.isPending;

  /**
   * Translate an `ApiRequestError` into either per-field errors (for
   * 422 changeset validation) or a single general error string. The
   * contract has three error shapes; this matches the two relevant
   * ones for auth:
   *   - 422: { errors: { email: ["…"], password: ["…"] } }
   *   - 401: { errors: { detail: "Unauthorized" } }
   */
  function classifyError(err: unknown): { fieldErrors: FieldErrors; general: string | null } {
    if (!(err instanceof ApiRequestError)) {
      return {
        fieldErrors: {},
        general: t("auth.errorGeneric"),
      };
    }

    if (err.status === 401) {
      return {
        fieldErrors: {},
        general: t("auth.errorCredentials"),
      };
    }

    if (err.status === 422) {
      const errors = err.body?.errors ?? {};
      const next: FieldErrors = {};
      let general: string | null = null;

      for (const [key, raw] of Object.entries(errors)) {
        const first = pickFirstMessage(raw);
        if (!first) continue;
        if (key === "email" || key === "password" || key === "name") {
          next[key] = first;
        } else {
          // `detail` (per Shape A) lands here — show it as a general
          // banner rather than tying it to a specific field.
          general = first;
        }
      }

      return { fieldErrors: next, general };
    }

    return { fieldErrors: {}, general: err.message };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (isPending) return;

    // Clear any previous error surface before a new attempt.
    setFieldErrors({});
    setGeneralError(null);

    try {
      if (mode === "register") {
        const res = await registerMutation.mutateAsync({ name, email, password });
        setSession(res.token, res.user);
        // Registration always produces a student.
        navigate("/calendar", { replace: true });
      } else {
        const res = await loginMutation.mutateAsync({ email, password });
        setSession(res.token, res.user);
        // Login redirects by role.
        navigate(
          res.user.role === "teacher" ? "/teacher" : "/calendar",
          { replace: true },
        );
      }
    } catch (err) {
      const { fieldErrors: nextFieldErrors, general } = classifyError(err);
      setFieldErrors(nextFieldErrors);
      setGeneralError(general);
    }
  }

  function clearErrorsOnEdit() {
    // Cheap UX win: typing should not look like "still wrong".
    if (Object.keys(fieldErrors).length > 0 || generalError) {
      setFieldErrors({});
      setGeneralError(null);
    }
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    setMode(next);
    setFieldErrors({});
    setGeneralError(null);
    // The name field is register-only; keep its value but it is never
    // sent on the login payload. Resetting the unused mutation is
    // harmless and keeps the dev experience honest if both are poked at.
    loginMutation.reset();
    registerMutation.reset();
  }

  const submitLabel = isPending
    ? mode === "login"
      ? t("auth.signingIn")
      : t("auth.creatingAccount")
    : mode === "login"
      ? t("auth.signIn")
      : t("auth.createAccount");

  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6) var(--space-4)",
      }}
    >
      <Card padding="var(--space-6)" style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: "var(--space-5)",
          }}
        >
          <img
            src={logoMark}
            alt=""
            width={44}
            height={44}
            style={{ flex: "none" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: "var(--fw-extra) var(--fs-2xl)/1 var(--font-display)",
                color: "var(--text-strong)",
                letterSpacing: "var(--ls-tight)",
              }}
            >
              TheCore Studio
            </div>
            <div
              style={{
                font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
                color: "var(--text-faint)",
                marginTop: 4,
              }}
            >
              {t("auth.tagline")}
            </div>
          </div>
          <LanguageSwitcher />
        </div>

        {/* mode toggle */}
        <div
          role="tablist"
          style={{
            display: "inline-flex",
            padding: 4,
            gap: 2,
            background: "var(--bg-sunken)",
            borderRadius: "var(--radius-pill)",
            marginBottom: "var(--space-5)",
          }}
        >
          {(["login", "register"] as const).map((m) => {
            const active = m === mode;
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => switchMode(m)}
                style={{
                  height: 36,
                  padding: "0 18px",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: "var(--radius-pill)",
                  font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
                  color: active ? "var(--text-on-brand)" : "var(--text-muted)",
                  background: active ? "var(--brand)" : "transparent",
                  boxShadow: active ? "var(--shadow-sm)" : "none",
                  transition:
                    "background var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)",
                }}
              >
                {m === "login" ? t("auth.signIn") : t("auth.createAccount")}
              </button>
            );
          })}
        </div>

        {showResetSuccess && (
          <Card
            role="status"
            padding="10px 14px"
            style={{
              background: "var(--status-success-bg)",
              border: "1px solid var(--sage-300)",
              boxShadow: "none",
              marginBottom: "var(--space-4)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--status-success-fg)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              <span style={{ display: "inline-flex", color: "var(--sage-600)" }}>
                <Icon name="check" size={18} />
              </span>
              {t("resetPassword.success")}
            </div>
          </Card>
        )}

        <form
          onSubmit={onSubmit}
          noValidate
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          {mode === "register" && (
            <Input
              label={t("auth.nameLabel")}
              name="name"
              autoComplete="name"
              placeholder={t("auth.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearErrorsOnEdit();
              }}
              required
              disabled={isPending}
              invalid={Boolean(fieldErrors.name)}
              hint={fieldErrors.name}
            />
          )}
          <Input
            label={t("auth.emailLabel")}
            type="email"
            name="email"
            autoComplete="email"
            placeholder={t("auth.emailPlaceholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearErrorsOnEdit();
            }}
            required
            autoFocus
            disabled={isPending}
            invalid={Boolean(fieldErrors.email)}
            hint={fieldErrors.email}
            iconLeft={<Icon name="user" size={18} />}
          />
          <PasswordInput
            label={t("auth.passwordLabel")}
            name="password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            placeholder={
              mode === "login"
                ? t("auth.passwordPlaceholderLogin")
                : t("auth.passwordPlaceholderRegister")
            }
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearErrorsOnEdit();
            }}
            required
            minLength={mode === "register" ? 12 : undefined}
            disabled={isPending}
            invalid={Boolean(fieldErrors.password)}
            hint={
              fieldErrors.password ??
              (mode === "register" ? t("auth.passwordHintRegister") : undefined)
            }
            iconLeft={<Icon name="settings" size={18} />}
          />

          {mode === "login" && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: -6,
              }}
            >
              <Link
                to="/forgot-password"
                id="forgot-password-link"
                style={{
                  font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
                  color: "var(--brand)",
                  textDecoration: "none",
                }}
              >
                {t("auth.forgotPasswordLink")}
              </Link>
            </div>
          )}

          {generalError && (
            <Card
              role="alert"
              padding="10px 14px"
              style={{
                background: "var(--status-danger-bg)",
                border: "1px solid var(--rust-500)",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  color: "var(--status-danger-fg)",
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                }}
              >
                <span style={{ display: "inline-flex", color: "var(--rust-500)" }}>
                  <Icon name="flame" size={18} />
                </span>
                {generalError}
              </div>
            </Card>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isPending}
          >
            {submitLabel}
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * Pull a human-readable message out of either a string[] (per-field
 * validation) or a string (the `detail` shape used by 401/403/404/500).
 */
function pickFirstMessage(raw: unknown): string | undefined {
  if (Array.isArray(raw)) {
    const first = raw[0];
    return typeof first === "string" ? first : undefined;
  }
  if (typeof raw === "string") return raw;
  return undefined;
}
